import type { ReactNode } from "react";

import { cx } from "@/lib/cx";
import { enterProps } from "@/ui/motion";
import { TONE_EDGE, TONE_SOFT } from "./tones";

export type AlertTone = "danger" | "streak" | "focus" | "accent";

export type AlertProps = {
  tone?: AlertTone;
  children: ReactNode;
  /** Trailing controls — typically a single "Dismiss" or "Retry" button. */
  actions?: ReactNode;
  className?: string;
};

/**
 * An inline notice about something that just happened — a write that failed, a
 * sync that could not complete.
 *
 * This exists because the same tinted box was hand-written in six places with
 * four different paddings and two different radii, so "a save failed" looked
 * like a different kind of event depending on which screen you were on. It is
 * deliberately *not* {@link StatusPanel}: that one owns whole-screen states
 * (empty, offline, broken), this one sits inside a working screen.
 *
 * It is the one place that always takes the `-edge` outline. An alert appears
 * *between* other content with no warning, so unlike a badge it has to have a
 * boundary of its own or it reads as the paragraph above it changing colour.
 */
export function Alert({ tone = "danger", children, actions, className }: AlertProps) {
  return (
    <div
      // Alerts describe a failure or interruption, so they fade into the
      // working context instead of using the status nudge reserved for neutral
      // and successful confirmation.
      {...enterProps("content")}
      role="alert"
      className={cx(
        "flex flex-wrap items-center justify-between gap-x-3 gap-y-2",
        // `rounded-surface` + `squircle`: an alert is a card inside the content
        // column, so it takes the grouped-card radius rather than the control
        // one — a notice the size of a paragraph wearing a 10px button radius
        // is the "one radius everywhere" tell (`IOS-DESIGN.md` §7).
        "rounded-surface px-4 py-3",
        TONE_SOFT[tone],
        TONE_EDGE[tone],
        className,
      )}
    >
      <p className="min-w-0 text-caption">{children}</p>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
